// src/components/application/settings/SettingsPage.js
import React from "react";
import {
    Paper, TextField, TableContainer, Table, TableHead, TableRow,
    TableCell, TableBody, Button, Box, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TableSortLabel,
    IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PageContainer from "../layout/PageContainer";
import useSettings from "../../hooks/useSettings";

// ---- Sort-Utils (stabil, Strings case-insensitiv) ----
function getCellValue(row, key) {
    switch (key) {
        case "key": return row.key ?? "";
        case "value": return row.value ?? "";
        default: return "";
    }
}
function descendingComparator(a, b, orderBy) {
    const sa = (getCellValue(a, orderBy) ?? "").toString().toLowerCase();
    const sb = (getCellValue(b, orderBy) ?? "").toString().toLowerCase();
    if (sb < sa) return -1;
    if (sb > sa) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
        const res = comparator(a[0], b[0]);
        if (res !== 0) return res;
        return a[1] - b[1];
    });
    return stabilized.map(el => el[0]);
}

const MODEL_KEY = "ai_default_model";
const AI_MODEL_OPTIONS = [
    "gemini-2.5-pro",
    "gemini-2.5-flash",
    "gemini-2.5-flash-lite",
];

export default function SettingsPage() {
    const { settings, loading, error, fetchSettings, upsertSetting, deleteSetting } = useSettings();

    // Sortierzustand
    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("key");

    // Suche
    const [q, setQ] = React.useState("");

    // Dialog / Formular
    const [open, setOpen] = React.useState(false);
    const [isEdit, setIsEdit] = React.useState(false);
    const [form, setForm] = React.useState({ key: "", value: "" });
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");

    // Delete confirm
    const [delOpen, setDelOpen] = React.useState(false);
    const [delKey, setDelKey] = React.useState("");
    const [delSubmitting, setDelSubmitting] = React.useState(false);
    const [delError, setDelError] = React.useState("");

    // Sortier-Handler
    const handleRequestSort = (property) => () => {
        setOrder(prev => (orderBy === property && prev === "asc" ? "desc" : "asc"));
        setOrderBy(property);
    };

    const handleOpenCreate = () => {
        setSubmitError("");
        setIsEdit(false);
        setForm({ key: "", value: "" });
        setOpen(true);
    };

    const handleOpenEdit = (row) => {
        setSubmitError("");
        setIsEdit(true);
        setForm({ key: row.key, value: row.value ?? "" });
        setOpen(true);
    };

    const handleSave = async () => {
        setSubmitError("");

        const key = (form.key || "").trim();
        const value = form.value ?? "";

        if (!key) { setSubmitError("Key ist Pflichtfeld."); return; }

        // English comment: Validate model selection if key is ai_default_model.
        if (key === MODEL_KEY && !AI_MODEL_OPTIONS.includes(String(value))) {
            setSubmitError("Bitte ein gültiges Modell auswählen.");
            return;
        }

        setSubmitting(true);
        try {
            await upsertSetting({ key, value: String(value) });
            setOpen(false);
            setForm({ key: "", value: "" });
        } catch (e) {
            setSubmitError(e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenDelete = (row) => {
        setDelError("");
        setDelKey(row.key);
        setDelOpen(true);
    };

    const handleConfirmDelete = async () => {
        setDelError("");
        setDelSubmitting(true);
        try {
            await deleteSetting(delKey);
            setDelOpen(false);
            setDelKey("");
        } catch (e) {
            setDelError(e.message || "Löschen fehlgeschlagen.");
        } finally {
            setDelSubmitting(false);
        }
    };

    const filteredRows = React.useMemo(() => {
        const list = Array.isArray(settings) ? settings : [];
        const needle = (q || "").toLowerCase();

        const filtered = !needle
            ? list
            : list.filter((r) => {
                const k = (r.key ?? "").toString().toLowerCase();
                const v = (r.value ?? "").toString().toLowerCase();
                return k.includes(needle) || v.includes(needle);
            });

        return stableSort(filtered, getComparator(order, orderBy));
    }, [settings, q, order, orderBy]);

    const columns = [
        { id: "key", label: "Key" },
        { id: "value", label: "Wert" },
        { id: "actions", label: "" },
    ];

    const isModelKey = (form.key || "").trim() === MODEL_KEY;

    return (
        <PageContainer
            title="Settings"
            breadcrumbs={["Home", "Settings"]}
            actions={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField size="small" label="Suche" value={q} onChange={(e) => setQ(e.target.value)} />
                    <Button variant="outlined" onClick={fetchSettings} disabled={loading}>Aktualisieren</Button>
                    <Button variant="contained" onClick={handleOpenCreate}>Neues Setting</Button>
                </Box>
            }
        >
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map(col => (
                                    <TableCell
                                        key={col.id}
                                        sortDirection={orderBy === col.id ? order : false}
                                        sx={{ userSelect: "none" }}
                                    >
                                        {col.id === "actions" ? null : (
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : "asc"}
                                                onClick={handleRequestSort(col.id)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredRows.map((row) => (
                                <TableRow key={row.key} hover>
                                    <TableCell sx={{ fontFamily: "monospace" }}>{row.key}</TableCell>
                                    <TableCell sx={{ whiteSpace: "pre-wrap" }}>{row.value ? row.value : <em>-</em>}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        <Tooltip title="Bearbeiten">
                                            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Löschen">
                                            <IconButton size="small" onClick={() => handleOpenDelete(row)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {!filteredRows.length && !loading && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                                        Keine Settings gefunden.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{isEdit ? "Setting bearbeiten" : "Neues Setting"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
                        <TextField
                            label="Key*"
                            value={form.key}
                            onChange={(e) => setForm(f => ({ ...f, key: e.target.value }))}
                            disabled={isEdit}
                            helperText={isEdit ? "Key kann beim Bearbeiten nicht geändert werden." : "z.B. ai_default_model"}
                        />

                        {isModelKey ? (
                            <FormControl fullWidth>
                                <InputLabel id="ai-model-label">Wert</InputLabel>
                                <Select
                                    labelId="ai-model-label"
                                    label="Wert"
                                    value={AI_MODEL_OPTIONS.includes(String(form.value)) ? String(form.value) : ""}
                                    onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))}
                                >
                                    {AI_MODEL_OPTIONS.map(m => (
                                        <MenuItem key={m} value={m}>{m}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <TextField
                                label="Wert"
                                value={form.value}
                                onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))}
                                multiline
                                minRows={3}
                            />
                        )}

                        {!!submitError && <Alert severity="error">{submitError}</Alert>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button onClick={handleSave} variant="contained" disabled={submitting}>
                        {submitting ? "Speichern…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog open={delOpen} onClose={() => setDelOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Setting löschen</DialogTitle>
                <DialogContent dividers>
                    <Alert severity="warning">
                        Willst du das Setting <b>{delKey}</b> wirklich löschen?
                    </Alert>
                    {!!delError && <Alert severity="error" sx={{ mt: 2 }}>{delError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDelOpen(false)} disabled={delSubmitting}>Abbrechen</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={delSubmitting}>
                        {delSubmitting ? "Löschen…" : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
